import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Settings.css';

function MfaSettings() {
  const { user, updateUser } = useAuth();
  const mfaEnabled = user?.mfaEnabled;

  // Disable MFA state
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableMsg, setDisableMsg] = useState(null);

  // Setup MFA state
  const [setupStarted, setSetupStarted] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [showManualKey, setShowManualKey] = useState(false);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [setupMsg, setSetupMsg] = useState(null);
  const [setupSuccess, setSetupSuccess] = useState(false);
  const inputRefs = useRef([]);

  const handleStartSetup = async () => {
    setSetupStarted(true);
    setSetupLoading(true);
    setSetupMsg(null);

    try {
      const data = await api.post('/api/auth/mfa/setup');
      setQrCode(data.qrCode);
      setManualKey(data.secret);
    } catch (err) {
      setSetupMsg({
        type: 'error',
        text: err.response?.data?.error?.message || 'Failed to initialize MFA setup.',
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDigitChange = (index, value) => {
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, '').slice(0, 6).split('');
      const newDigits = [...digits];
      pasted.forEach((d, i) => {
        if (index + i < 6) newDigits[index + i] = d;
      });
      setDigits(newDigits);
      const nextIndex = Math.min(index + pasted.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = digits.join('');

    if (code.length !== 6) {
      setSetupMsg({ type: 'error', text: 'Please enter all 6 digits.' });
      return;
    }

    setVerifyLoading(true);
    setSetupMsg(null);

    try {
      await api.post('/api/auth/mfa/confirm-setup', { code });
      updateUser({ mfaEnabled: true });
      setSetupSuccess(true);
    } catch (err) {
      setSetupMsg({
        type: 'error',
        text: err.response?.data?.error?.message || 'Invalid code. Please try again.',
      });
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    setDisableLoading(true);
    setDisableMsg(null);

    try {
      await api.post('/api/auth/mfa/disable');
      updateUser({ mfaEnabled: false });
      setShowDisableConfirm(false);
      setDisableMsg({ type: 'success', text: 'MFA has been disabled.' });
    } catch (err) {
      setDisableMsg({
        type: 'error',
        text: err.response?.data?.error?.message || 'Failed to disable MFA.',
      });
      setShowDisableConfirm(false);
    } finally {
      setDisableLoading(false);
    }
  };

  // Auto-focus first digit when setup is ready
  useEffect(() => {
    if (qrCode && inputRefs.current[0]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [qrCode]);

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Multi-Factor Authentication</h1>
        <p>Add an extra layer of security to your account</p>
      </div>

      {/* MFA Status Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-icon mfa">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <div className="settings-card-header-text">
            <h2>Authentication Status</h2>
            <p>Manage your two-factor authentication settings</p>
          </div>
        </div>

        {(disableMsg || setupMsg) && (
          <div
            className={`settings-alert ${(disableMsg || setupMsg).type}`}
            style={{ marginBottom: 20 }}
          >
            {(disableMsg || setupMsg).type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <span>{(disableMsg || setupMsg).text}</span>
          </div>
        )}

        {mfaEnabled && !setupSuccess ? (
          /* ── MFA is currently enabled ─────────────────────────────── */
          <>
            <div style={{ marginBottom: 20 }}>
              <span className="mfa-status-badge enabled">
                <span className="status-dot" />
                Enabled
              </span>
            </div>

            <div className="mfa-status-info">
              <div className="mfa-status-row">
                <span className="mfa-status-row-label">Method</span>
                <span className="mfa-status-row-value">Authenticator App (TOTP)</span>
              </div>
              <div className="mfa-status-row">
                <span className="mfa-status-row-label">Status</span>
                <span className="mfa-status-row-value">Active and protecting your account</span>
              </div>
            </div>

            <div className="mfa-disable-section">
              <p>
                Disabling MFA will remove the extra layer of security from your account.
                You will only need your password to sign in.
              </p>
              <button
                className="settings-btn-danger"
                onClick={() => setShowDisableConfirm(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <line x1="8" y1="11" x2="16" y2="11" />
                </svg>
                Disable MFA
              </button>
            </div>
          </>
        ) : setupSuccess ? (
          /* ── Just enabled MFA successfully ─────────────────────────── */
          <div className="mfa-success-state">
            <div className="success-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3>MFA Enabled Successfully</h3>
            <p>
              Your account is now protected with two-factor authentication.
              You&apos;ll need your authenticator app each time you sign in.
            </p>
          </div>
        ) : !setupStarted ? (
          /* ── MFA is not enabled - prompt to set up ────────────────── */
          <>
            <div style={{ marginBottom: 20 }}>
              <span className="mfa-status-badge disabled">
                <span className="status-dot" />
                Not Enabled
              </span>
            </div>

            <div className="mfa-status-info">
              <div className="mfa-status-row">
                <span className="mfa-status-row-label">Recommendation</span>
                <span className="mfa-status-row-value">Enable MFA for stronger account security</span>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 16px' }}>
                Two-factor authentication adds an extra layer of security by requiring a code
                from your authenticator app in addition to your password when signing in.
              </p>
              <button className="settings-btn-primary" onClick={handleStartSetup}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Set Up MFA
              </button>
            </div>
          </>
        ) : (
          /* ── MFA setup flow ───────────────────────────────────────── */
          <div className="mfa-setup-embedded">
            {setupLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <div className="loading-spinner" style={{ width: 32, height: 32, border: '3px solid rgba(5,153,141,0.2)', borderTopColor: '#05998d', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              </div>
            ) : (
              <>
                <div className="mfa-setup-step">
                  <div className="mfa-step-number">1</div>
                  <div className="mfa-step-content">
                    <h3>Scan QR Code</h3>
                    <p>Open your authenticator app (Google Authenticator, Authy, etc.) and scan the code below.</p>
                  </div>
                </div>

                {qrCode && (
                  <div className="mfa-qr-container">
                    <img src={qrCode} alt="MFA QR Code" className="mfa-qr-code" />
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setShowManualKey(!showManualKey)}
                      style={{ marginTop: 12 }}
                    >
                      {showManualKey ? 'Hide manual key' : "Can't scan? Enter key manually"}
                    </button>
                    {showManualKey && (
                      <div className="mfa-manual-key">
                        <code>{manualKey}</code>
                      </div>
                    )}
                  </div>
                )}

                <div className="mfa-setup-step" style={{ marginTop: 24 }}>
                  <div className="mfa-step-number">2</div>
                  <div className="mfa-step-content">
                    <h3>Enter Verification Code</h3>
                    <p>Type the 6-digit code from your authenticator app to confirm setup.</p>
                  </div>
                </div>

                <form onSubmit={handleVerify}>
                  <div className="mfa-code-inputs" style={{ margin: '20px 0' }}>
                    {digits.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        className="mfa-digit"
                        value={digit}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>

                  <div className="settings-actions" style={{ justifyContent: 'center' }}>
                    <button
                      type="button"
                      className="settings-btn-secondary"
                      onClick={() => {
                        setSetupStarted(false);
                        setQrCode('');
                        setManualKey('');
                        setDigits(['', '', '', '', '', '']);
                        setSetupMsg(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="settings-btn-primary" disabled={verifyLoading}>
                      {verifyLoading && <span className="settings-spinner" />}
                      {verifyLoading ? 'Verifying...' : 'Enable MFA'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>

      {/* Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3>Disable MFA?</h3>
            <p>
              This will remove two-factor authentication from your account.
              Anyone with your password will be able to sign in without a second verification step.
            </p>
            <div className="confirm-dialog-actions">
              <button
                className="settings-btn-secondary"
                onClick={() => setShowDisableConfirm(false)}
                disabled={disableLoading}
              >
                Cancel
              </button>
              <button
                className="settings-btn-danger"
                onClick={handleDisableMfa}
                disabled={disableLoading}
              >
                {disableLoading && <span className="settings-spinner dark" />}
                {disableLoading ? 'Disabling...' : 'Yes, Disable MFA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MfaSettings;
